import type { NpcInstanceV1 } from "@cold-case/shared/case-manifest";
import Phaser from "phaser";

import { FollowNudgePhaserChrome } from "../lib/follow-nudge-ui.js";
import { buildNpcCaseNotes, type NpcCaseNotes } from "../lib/npc-case-notes.js";
import { isFollowingLeader } from "./player-follow.js";
import { setPlayerOverlayOpen } from "../lib/player-input-gate.js";

import {
  bodyViewportHeight,
  computeDialogShell,
  type DialogShellMetrics,
} from "./dialog-layout.js";
import {
  caseNpcLineStyle,
  createNpcHeader,
  measureNpcHeader,
  queueSouthPortraitIfMissing,
  southPortraitTextureKey,
  type NpcHeaderObjects,
} from "./npc-dialog-chrome.js";
import { MaskedScrollViewport } from "./npc-dialog-scroll.js";
import { UI_FONT, UI_SURFACE, UI_TEXT } from "./ui-theme.js";

const Z = 4999;
const FOOTER_H = 108;

export type NpcCaseNotesOpenPayload = {
  npc: NpcInstanceV1;
  onBeginInterview: () => void;
  onCancel: () => void;
};

export class NpcCaseNotesPanel {
  private readOnly = false;
  private dim: Phaser.GameObjects.Rectangle | null = null;
  private panel: Phaser.GameObjects.Rectangle | null = null;
  private header: NpcHeaderObjects | null = null;
  private npcSkinId = "";
  private headerBottom = 0;
  private bodyBg: Phaser.GameObjects.Rectangle | null = null;
  private bodyContainer: Phaser.GameObjects.Container | null = null;
  private bodyMask: Phaser.GameObjects.Graphics | null = null;
  private actionRows: Phaser.GameObjects.Text[] = [];
  private scroll: MaskedScrollViewport | null = null;
  private shell: DialogShellMetrics | null = null;
  private notes: NpcCaseNotes | null = null;
  private headerH = 0;
  private bodyTop = 0;
  private bodyH = 0;
  private bodyLeft = 0;
  private beginY = 0;
  private cancelY = 0;
  private onBegin: (() => void) | null = null;
  private onCancel: (() => void) | null = null;
  private nudgeChrome: FollowNudgePhaserChrome | null = null;

  constructor(private readonly scene: Phaser.Scene) {}

  get isOpen(): boolean {
    return this.dim !== null;
  }

  flashNudgedAck(): void {
    this.nudgeChrome?.showNudgedAck();
  }

  open(params: NpcCaseNotesOpenPayload): void {
    this.close();
    this.readOnly = false;
    this.onBegin = params.onBeginInterview;
    this.onCancel = params.onCancel;
    this.notes = buildNpcCaseNotes(params.npc);
    this.npcSkinId = params.npc.npc_id;
    queueSouthPortraitIfMissing(this.scene, this.npcSkinId, () => this.relayout());
    setPlayerOverlayOpen("npc-case-notes", true);
    this.buildUi();
  }

  /** Follower mirror — case notes visible, actions disabled. */
  openMirror(params: NpcCaseNotesOpenPayload): void {
    this.open(params);
    this.readOnly = true;
    this.relayout();
  }

  relayout(): void {
    if (!this.isOpen || !this.notes) return;
    const savedScrollY = this.scroll?.scrollY ?? 0;
    const savedStick = this.scroll?.stickToBottom ?? false;
    this.destroyUi();
    this.buildUi();
    if (this.scroll) {
      this.scroll.stickToBottom = savedStick;
      this.scroll.scrollY = savedScrollY;
      this.scroll.reflow();
    }
  }

  close(): void {
    if (!this.isOpen) return;
    this.destroyUi();
    setPlayerOverlayOpen("npc-case-notes", false);
    this.onBegin = null;
    this.onCancel = null;
    this.notes = null;
    this.shell = null;
  }

  cancel(): void {
    const cb = this.onCancel;
    this.close();
    cb?.();
  }

  beginInterview(): void {
    this.onBegin?.();
  }

  private destroyUi(): void {
    this.nudgeChrome?.destroy();
    this.nudgeChrome = null;
    this.scroll?.unbind();
    this.scroll = null;
    this.dim?.destroy();
    this.panel?.destroy();
    this.header?.title.destroy();
    this.header?.portrait?.destroy();
    this.header = null;
    this.bodyBg?.destroy();
    this.bodyMask?.destroy();
    this.bodyContainer?.destroy();
    for (const t of this.actionRows) t.destroy();
    this.actionRows = [];
    this.dim = null;
    this.panel = null;
    this.bodyBg = null;
    this.bodyMask = null;
    this.bodyContainer = null;
  }

  private layoutMetrics(shell: DialogShellMetrics, notes: NpcCaseNotes): void {
    const portraitKey = southPortraitTextureKey(this.scene, this.npcSkinId);
    this.headerBottom =
      measureNpcHeader(this.scene, shell, notes.subjectName, portraitKey).headerBottom -
      shell.panelTop;
    this.headerH = this.headerBottom;
    this.bodyTop = shell.panelTop + this.headerH;
    this.bodyH = bodyViewportHeight(shell.panelH, this.headerH, FOOTER_H);
    this.bodyLeft = shell.panelCx - shell.innerW * 0.5;

    const beginProbe = this.scene.add.text(0, 0, "Begin interview", {
      fontFamily: UI_FONT,
      fontSize: `${shell.buttonFont}px`,
      padding: { x: 18, y: 12 },
    });
    const cancelProbe = this.scene.add.text(0, 0, "Not now", {
      fontFamily: UI_FONT,
      fontSize: `${shell.buttonFont}px`,
      padding: { x: 18, y: 10 },
    });
    const beginH = beginProbe.height;
    const cancelH = cancelProbe.height;
    beginProbe.destroy();
    cancelProbe.destroy();

    const btnGap = 10;
    const panelBottom = shell.panelBottom;
    this.cancelY = panelBottom - shell.pad - cancelH;
    this.beginY = this.cancelY - btnGap - beginH;
  }

  private buildUi(): void {
    const notes = this.notes!;
    const vw = this.scene.scale.width;
    const vh = this.scene.scale.height;
    const shell = computeDialogShell(vw, vh, FOOTER_H);
    this.shell = shell;
    this.layoutMetrics(shell, notes);

    this.dim = this.scene.add
      .rectangle(vw * 0.5, vh * 0.5, vw, vh, shell.dimColor, shell.dimAlpha)
      .setScrollFactor(0)
      .setDepth(Z)
      .setInteractive();

    this.panel = this.scene.add
      .rectangle(shell.panelCx, shell.panelCy, shell.panelW, shell.panelH, UI_SURFACE.panel, 0.98)
      .setStrokeStyle(2, UI_SURFACE.panelStroke, 1)
      .setScrollFactor(0)
      .setDepth(Z + 1)
      .setInteractive();

    const portraitKey = southPortraitTextureKey(this.scene, this.npcSkinId);
    this.header = createNpcHeader(this.scene, shell, Z + 2, notes.subjectName, portraitKey);
    this.headerBottom = this.header.metrics.headerBottom - shell.panelTop;
    this.headerH = this.headerBottom;
    this.bodyTop = shell.panelTop + this.headerH;
    this.bodyH = bodyViewportHeight(shell.panelH, this.headerH, FOOTER_H);

    this.bodyBg = this.scene.add
      .rectangle(
        this.bodyLeft + shell.innerW * 0.5,
        this.bodyTop + this.bodyH * 0.5,
        shell.innerW,
        this.bodyH,
        UI_SURFACE.transcript,
        1
      )
      .setStrokeStyle(1, UI_SURFACE.transcriptStroke, 1)
      .setScrollFactor(0)
      .setDepth(Z + 2);

    this.bodyContainer = this.scene.add
      .container(this.bodyLeft, this.bodyTop)
      .setScrollFactor(0)
      .setDepth(Z + 3);

    this.bodyMask = this.scene.add.graphics().setScrollFactor(0).setDepth(Z + 3);
    this.bodyMask.fillStyle(0xffffff, 1);
    this.bodyMask.fillRect(this.bodyLeft, this.bodyTop, shell.innerW, this.bodyH);
    this.bodyMask.setVisible(false);
    this.bodyContainer.setMask(this.bodyMask.createGeometryMask());

    const bodyText = this.scene.add.text(0, 6, notes.personality, {
      ...caseNpcLineStyle(shell.bodyFont),
      wordWrap: { width: shell.innerW - 12 },
    });
    bodyText.setResolution(2);
    this.bodyContainer.add(bodyText);

    this.scroll = new MaskedScrollViewport({
      scene: this.scene,
      hitTarget: this.bodyBg,
      getContainer: () => this.bodyContainer,
      getViewportTop: () => this.bodyTop,
      getViewportH: () => this.bodyH,
      getLineGap: () => shell.lineGap,
    });
    this.scroll.stickToBottom = false;
    this.scroll.bind();
    this.scroll.reflow({ forceBottom: false });

    this.renderActions(shell);
    this.syncNudgeChrome(shell);
  }

  private syncNudgeChrome(shell: DialogShellMetrics): void {
    this.nudgeChrome?.destroy();
    this.nudgeChrome = new FollowNudgePhaserChrome(this.scene, Z + 6);
    if (this.readOnly && isFollowingLeader()) {
      this.nudgeChrome.mountFollowerNudge(shell);
    } else if (!this.readOnly) {
      this.nudgeChrome.mountLeaderAck(shell);
    } else {
      this.nudgeChrome.destroy();
      this.nudgeChrome = null;
    }
  }

  private renderActions(shell: DialogShellMetrics): void {
    const choiceW = shell.panelW - shell.pad * 2;

    const begin = this.scene.add
      .text(shell.panelCx, this.beginY, "Begin interview", {
        fontFamily: UI_FONT,
        fontSize: `${shell.buttonFont}px`,
        color: this.readOnly ? UI_TEXT.muted : UI_TEXT.primary,
        fontStyle: "600",
        backgroundColor: this.readOnly ? UI_SURFACE.btnGhost : UI_SURFACE.btnPrimary,
        padding: { x: 18, y: 12 },
        align: "center",
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(Z + 5);
    begin.setWordWrapWidth(choiceW - 24, true);
    if (!this.readOnly) {
      begin.setInteractive({ useHandCursor: true });
      begin.on("pointerdown", (
        _pointer: Phaser.Input.Pointer,
        _lx: number,
        _ly: number,
        event: Phaser.Types.Input.EventData
      ) => {
        event.stopPropagation();
        this.beginInterview();
      });
    }
    this.actionRows.push(begin);

    const cancel = this.scene.add
      .text(shell.panelCx, this.cancelY, "Not now", {
        fontFamily: UI_FONT,
        fontSize: `${shell.buttonFont}px`,
        color: this.readOnly ? UI_TEXT.muted : UI_TEXT.link,
        fontStyle: "600",
        backgroundColor: this.readOnly ? UI_SURFACE.btnGhost : UI_SURFACE.btnSecondary,
        padding: { x: 18, y: 10 },
        align: "center",
      })
      .setOrigin(0.5, 0)
      .setScrollFactor(0)
      .setDepth(Z + 5);
    if (!this.readOnly) {
      cancel.setInteractive({ useHandCursor: true });
      cancel.on("pointerdown", (
        _pointer: Phaser.Input.Pointer,
        _lx: number,
        _ly: number,
        event: Phaser.Types.Input.EventData
      ) => {
        event.stopPropagation();
        this.cancel();
      });
    }
    this.actionRows.push(cancel);
  }
}
